/**
 * Hook that sends a user turn to the Janus DATA agent and streams the
 * answer back into `useJanusAgentStore`.
 *
 * Transport: reuses `agentApi.chatStream` (POST `/api/v1/agent/chat/stream`)
 * — the exact same streaming client the full-page `/agent` chat uses, so
 * the widget renders identical rich content (markdown, janus: cards, tool
 * trace). Everything goes through `/api/v1` (the BFF); no backend hostname
 * is ever contacted directly.
 *
 * Hand-off detection
 * ------------------
 * The data agent can decide a question is really about the platform
 * (how-to / billing / settings / RBAC) and signal that the Support
 * Assistant should take over. We light up an inline "Continuar no
 * Assistente JanusOS →" affordance when EITHER channel carries the flag:
 *
 *   1. The terminal `done` SSE event includes `handoff_to_assistant: true`
 *      (see `AgentDonePayload` in agentApi) — the primary contract.
 *   2. A `tool_step` whose args/summary expose a truthy `handoff_to_assistant`
 *      — a tolerant fallback in case the backend routes the signal through a
 *      tool call instead.
 *
 * Both are OPTIONAL. When the flag is absent (the norm today) nothing
 * changes and the conversation behaves exactly like the full-page agent.
 */

import { useCallback } from 'react';
import {
  agentApi,
  type AgentToolStep,
  type AgentDonePayload,
} from '../../modules/agent/services/agentApi';
import { useJanusAgentStore } from '../stores/useJanusAgentStore';

/** Read a truthy `handoff_to_assistant` from a tool step's args or summary. */
function toolStepRequestsHandoff(step: AgentToolStep): boolean {
  const fromArgs = step.args?.['handoff_to_assistant'];
  if (fromArgs === true) return true;
  // Some tool surfaces stringify metadata into the summary; sniff defensively.
  if (typeof step.summary === 'string' && /handoff_to_assistant["':\s]+true/i.test(step.summary)) {
    return true;
  }
  return false;
}

export function useJanusAgentChat() {
  const sessionId = useJanusAgentStore((s) => s.sessionId);
  const isSending = useJanusAgentStore((s) => s.isSending);
  const appendUserMessage = useJanusAgentStore((s) => s.appendUserMessage);
  const appendAssistantPlaceholder = useJanusAgentStore((s) => s.appendAssistantPlaceholder);
  const appendAssistantChunk = useJanusAgentStore((s) => s.appendAssistantChunk);
  const appendToolStep = useJanusAgentStore((s) => s.appendToolStep);
  const patchAssistantMessage = useJanusAgentStore((s) => s.patchAssistantMessage);
  const setSending = useJanusAgentStore((s) => s.setSending);
  const setError = useJanusAgentStore((s) => s.setError);

  const send = useCallback(
    async (rawMessage: string) => {
      const message = (rawMessage ?? '').trim();
      if (!message) return;
      if (useJanusAgentStore.getState().isSending) return;

      setError(null);
      setSending(true);
      appendUserMessage(message);
      const assistantId = appendAssistantPlaceholder();

      // Track whether a hand-off was requested via any channel during this turn.
      let handoffRequested = false;

      await new Promise<void>((resolve) => {
        agentApi.chatStream(
          { message, session_id: sessionId },
          {
            onToken: (token) => appendAssistantChunk(assistantId, token),
            onToolStep: (step) => {
              appendToolStep(assistantId, step);
              if (toolStepRequestsHandoff(step)) handoffRequested = true;
            },
            onDone: (payload?: AgentDonePayload) => {
              if (payload?.handoff_to_assistant === true) handoffRequested = true;
              patchAssistantMessage(assistantId, {
                isStreaming: false,
                handoff: handoffRequested,
              });
              setSending(false);
              resolve();
            },
            onError: (err) => {
              patchAssistantMessage(assistantId, {
                content:
                  useJanusAgentStore.getState().messages.find((m) => m.id === assistantId)
                    ?.content ||
                  'O Agente JanusOS está temporariamente indisponível. Tente novamente em instantes.',
                isStreaming: false,
              });
              setError(err.message || 'unknown_error');
              setSending(false);
              resolve();
            },
          },
        );
      });
    },
    [
      sessionId,
      appendUserMessage,
      appendAssistantPlaceholder,
      appendAssistantChunk,
      appendToolStep,
      patchAssistantMessage,
      setSending,
      setError,
    ],
  );

  return { send, isSending };
}
